import _ from "lodash";
import React, { isValidElement, cloneElement } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";

import { Tooltip } from "@toddle-edu/ds-web";
import { InformationFilled } from "@toddle-edu/ds-icons";
import UIBaseComponent from "UIComponents/UIBaseComponent";

import {
  updateAssessment,
  updateAssessmentInRedux,
  writeAssessmentWeightageInCache,
} from "Assessments/modules/AssessmentModule";
import { getScoreDetailsMemoize } from "ScoreCategories/modules/ScoreCategoriesModule";
import { DOUBLE_DIGIT_DECIMAL_NUMBERS_REGEX } from "Constants";
import classes from "./TaskWeightV3.scss";

const styles = {
  overlayInnerStyle: {
    maxWidth: "264px",
    padding: "8px",
    textAlign: "center",
    zIndex: "1",
  },
};

class TaskWeightV3 extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      taskWeight: "",
      isError: false,
    };

    if (props?.customRef) {
      props.customRef(this);
    }
  }

  isValid = () => {
    const { assessmentTools, isCollapsibleAssessmentTool } = this.props;
    const { taskWeight, isError } = this.state;

    if (isCollapsibleAssessmentTool) {
      return 0;
    }

    const { maxScore } = getScoreDetailsMemoize({
      assessmentTools,
    });
    if (!_.isEmpty(maxScore) && _.isEmpty(_.toString(taskWeight))) {
      this.updateState({ key: "isError", value: true });
      return 1;
    }

    if (isError) {
      this.updateState({ key: "isError", value: false });
    }
    return 0;
  };

  componentDidMount = () => {
    const { value } = this.props;
    this.updateState({ key: "taskWeight", value });
  };

  updateState = ({ key, value }) => {
    this.setState(prevState => {
      return {
        ...prevState,
        [key]: value,
      };
    });
  };

  debouncedUpdateWeightageInCache = _.debounce(weightage => {
    const {
      writeAssessmentWeightageInCache,
      assessmentId,
      parentId,
    } = this.props;

    writeAssessmentWeightageInCache({
      assessmentId: assessmentId || parentId,
      weightage,
    });
  }, 300);

  updateTaskWeight = e => {
    const { isError } = this.state;
    const weight = _.get(e, "target.value");
    const weightFloat = parseFloat(weight);

    if (_.isNaN(weightFloat)) {
      this.updateState({ key: "taskWeight", value: "" });
      return;
    }

    // allow only numbers and upto 2 decimals
    if (!DOUBLE_DIGIT_DECIMAL_NUMBERS_REGEX.test(weight) && weight) {
      return;
    }

    const weightage =
      weightFloat || !weight || _.size(weight) > 0 ? weight : "";

    this.updateState({
      key: "taskWeight",
      value: weightage,
    });

    if (isError) {
      this.updateState({ key: "isError", value: false });
    }

    this.debouncedUpdateWeightageInCache(weightage);
  };

  onBlur = () => {
    const { updateAssessment } = this.props;
    const { taskWeight } = this.state;

    if (_.includes(taskWeight, ".")) {
      this.updateState({
        key: "taskWeight",
        value: _.toString(parseFloat(taskWeight)),
      });
    }

    updateAssessment({
      hasFields: false,
      weightage: taskWeight ? parseFloat(taskWeight) : null,
    });
  };

  renderTaskWeight = () => {
    const { t, mode, testId } = this.props;
    const { taskWeight, isError } = this.state;
    const isViewMode = mode === "view";

    return (
      <input
        type="text"
        id={testId}
        data-test-id={testId}
        value={taskWeight}
        onChange={this.updateTaskWeight}
        onBlur={this.onBlur}
        disabled={isViewMode}
        aria-live="polite"
        pattern="[0-9]*"
        autoComplete="off"
        placeholder={`${t("common:enter_text")} (${t("common:eg_100")})`}
        className={classNames("text-body-l", classes.taskWeightageInput, {
          [classes.taskWeightageInputError]: isError,
          [classes.taskWeightageInputDisabled]: isViewMode,
        })}
      />
    );
  };

  renderEdit = this.renderTaskWeight;
  renderView = this.renderTaskWeight;
}

const mapActionCreators = {
  updateAssessment,
  updateAssessmentInRedux,
  writeAssessmentWeightageInCache,
};

const mapStateToProps = (state, ownProps) => {
  const { t, labelComponent: _labelComponent, label, weightage } = ownProps;

  const labelRightIcon = (
    <Tooltip
      placement={"bottom"}
      tooltip={t("classRoom:task_weight_tooltip_message")}
      overlayInnerStyle={styles.overlayInnerStyle}
      showArrow={true}
    >
      <InformationFilled variant={"subtle"} size={"xxx-small"} />
    </Tooltip>
  );

  const labelComponent = isValidElement(_labelComponent)
    ? cloneElement(_labelComponent, {
        label,
        labelRightIcon,
        labelContainerStyle: {
          width: "240px",
          justifyContent: "center",
        },
      })
    : null;

  return {
    labelComponent,
    value: weightage,
  };
};

TaskWeightV3.propTypes = {
  ...UIBaseComponent.propTypes,
  isCollapsibleAssessmentTool: PropTypes.bool,
};

TaskWeightV3.defaultProps = {
  ...UIBaseComponent.defaultProps,
  isCollapsibleAssessmentTool: false,
};

export default connect(mapStateToProps, mapActionCreators)(TaskWeightV3);
